// Operator › System agents: issue or rotate a system agent's own runtime key (`cosk_sa_`, super
// admins only; `POST …/key` revokes the active key and returns the new plaintext once). The
// plaintext lives only in this dialog's state — never in the mutation cache, a query, storage or
// the console — and leaves memory when the dialog closes. While it is shown the dialog closes
// only through "Done" after "I've copied it".
import {
  Button,
  Checkbox,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldDescription,
  FieldLabel,
  Input,
  Label,
} from "@companyos/ui";
import { useMutation } from "@tanstack/react-query";
import { CopyIcon, KeyRoundIcon } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import type { Api } from "../../api/client";

type Shown = { prefix: string; plaintext: string; scopes: string[]; revoked: number };

export function SystemAgentKeyDialog({
  api,
  companyId,
  membershipId,
  name,
}: {
  api: Api;
  companyId: string;
  membershipId: string;
  name: string;
}) {
  const [open, setOpen] = useState(false);
  const [shown, setShown] = useState<Shown | null>(null);
  const [copied, setCopied] = useState(false);
  const [copyState, setCopyState] = useState<"idle" | "done" | "failed">("idle");
  const plaintextId = useId();
  const confirmId = useId();

  // Each opening is a session; a response landing after its session ended is discarded.
  const session = useRef(0);
  useEffect(
    () => () => {
      session.current += 1;
    },
    [],
  );

  const issue = useMutation({
    // Only the non-secret prefix reaches the mutation result; the plaintext goes to local state.
    mutationFn: async (): Promise<string> => {
      const mine = session.current;
      const r = await api.client.operator.systemAgents.key.issue({
        params: { company_id: companyId, membership_id: membershipId },
        body: {},
      });
      if (mine === session.current) {
        setShown({ prefix: r.key_prefix, plaintext: r.key, scopes: r.scopes, revoked: r.revoked });
      }
      return r.key_prefix;
    },
  });

  const reset = () => {
    session.current += 1;
    setShown(null);
    setCopied(false);
    setCopyState("idle");
    issue.reset();
  };
  const onOpenChange = (next: boolean) => {
    if (!next && issue.isPending) return;
    if (!next && shown && !copied) return;
    setOpen(next);
    if (!next) reset();
  };
  const copy = async () => {
    if (!shown) return;
    try {
      await navigator.clipboard.writeText(shown.plaintext);
      setCopyState("done");
    } catch {
      setCopyState("failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Button
        variant="ghost"
        size="sm"
        data-slot="system-agent-key"
        aria-label={`Issue or rotate ${name}'s runtime key`}
        title="Issue or rotate runtime key"
        onClick={() => setOpen(true)}
      >
        <KeyRoundIcon aria-hidden="true" />
        Key
      </Button>
      <DialogContent showCloseButton={!shown && !issue.isPending} className="sm:max-w-lg">
        {shown ? (
          <>
            <DialogHeader>
              <DialogTitle>Copy the new key</DialogTitle>
              <DialogDescription>
                Shown only now. Put it in {name}'s runtime secrets; CompanyOS keeps only the prefix{" "}
                {shown.prefix}.{shown.revoked > 0 ? " The previous key is revoked." : ""}
              </DialogDescription>
            </DialogHeader>
            <Field>
              <FieldLabel htmlFor={plaintextId}>Runtime key</FieldLabel>
              <div className="flex min-w-0 gap-2">
                <Input
                  id={plaintextId}
                  readOnly
                  autoComplete="off"
                  value={shown.plaintext}
                  className="min-w-0 font-mono text-xs"
                  onFocus={(e) => e.currentTarget.select()}
                />
                <Button variant="outline" onClick={() => void copy()}>
                  <CopyIcon aria-hidden="true" />
                  Copy
                </Button>
              </div>
              <FieldDescription aria-live="polite">
                {copyState === "done"
                  ? "Copied to the clipboard."
                  : copyState === "failed"
                    ? "Copy failed: select the key and copy it by hand."
                    : `Scopes: ${shown.scopes.join(", ")}`}
              </FieldDescription>
            </Field>
            <div className="flex items-center gap-2">
              <Checkbox
                id={confirmId}
                checked={copied}
                onCheckedChange={(v) => setCopied(v === true)}
              />
              <Label htmlFor={confirmId}>I've copied it</Label>
            </div>
            <DialogFooter>
              <Button disabled={!copied} onClick={() => onOpenChange(false)}>
                Done
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>{name}'s runtime key</DialogTitle>
              <DialogDescription>
                Issues a new key and revokes the active one. The key is shown once.
              </DialogDescription>
            </DialogHeader>
            {issue.isError ? (
              <p role="alert" className="text-destructive text-sm">
                The key was not issued. Try again.
              </p>
            ) : null}
            <DialogFooter>
              <DialogClose render={<Button variant="outline" />} disabled={issue.isPending}>
                Cancel
              </DialogClose>
              <Button disabled={issue.isPending} onClick={() => issue.mutate()}>
                {issue.isPending ? "Issuing…" : "Issue key"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
